/**
 * Variant Picker - Custom
 * Goal: update variant id + price + button state client-side
 *
 * Relies on:
 * - <variant-selects> markup from snippets/variant-picker-custom.liquid
 * - price HTML map embedded in the purchase panel container
 * - <product-form> or <product-form-custom> custom element for add-to-cart + button text formatting
 */

(() => {
  // Selectors
  const SELECTOR = '[data-variant-picker-custom]';
  const OPTION_SELECTOR = 'input[type="radio"][name^="options["], select[name^="options["]';
  const VARIANT_SELECTS_TAG = 'variant-selects';
  const VARIANTS_SCRIPT_SELECTOR = 'script[data-variant-picker-custom-variants]';
  const PRICE_SCRIPT_SELECTOR = 'script[data-variant-picker-custom-price-html]';
  const FORM_SELECTOR = 'form[action*="/cart/add"]';
  const ID_INPUT_SELECTOR = 'input[name="id"]';
  const PRICE_CONTAINER_SELECTOR = '[data-variant-picker-custom-price]';
  const SUBMIT_BUTTON_SELECTOR = '[type="submit"][name="add"]';
  const AUTO_DISCOUNT_SELECTOR = '[data-auto-discount="true"]';

  // Image selectors
  const PRELOADER_SELECTOR = '[data-vpc-preloader]';
  const IMAGE_SELECTOR = '[data-vpc-img]';
  const IMAGE_WRAPPER_SELECTOR = '[data-vpc-img-wrapper]';

  // Attributes
  const ATTR_NAME = 'name';
  const ATTR_DATA_PRICE = 'data-price';
  const ATTR_DATA_ORIGINAL_PRICE = 'data-original-price';
  const ATTR_DATA_AUTO_DISCOUNT = 'data-auto-discount';
  const ATTR_DATA_SHOW_PRICE_IN_BUTTON = 'data-show-price-in-button';
  const ATTR_DISABLED = 'disabled';

  // CSS Classes
  const CLASS_HIDE = 'hide';
  const CLASS_DISABLED = 'disabled';

  // Default values
  const DEFAULT_NULL_STRING = 'null';
  const DEFAULT_SOLD_OUT = 'Sold out';
  const DEFAULT_UNAVAILABLE = 'Unavailable';

  function parseJSONScript(root, selector) {
    const el = root.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || DEFAULT_NULL_STRING);
    } catch {
      return null;
    }
  }

  function getSelectedOptions(variantSelectsEl) {
    const optionInputs = variantSelectsEl.querySelectorAll(OPTION_SELECTOR);
    const byName = new Map();
    const orderedNames = [];

    optionInputs.forEach((el) => {
      const name = el.getAttribute(ATTR_NAME);
      if (!name) return;

      // Collect ordered names (preserve DOM order)
      if (!orderedNames.includes(name)) orderedNames.push(name);

      // Collect selected values
      if (el.tagName === 'SELECT') {
        byName.set(name, el.value);
      } else if (el.checked) {
        byName.set(name, el.value);
      }
    });

    // Return in option order (option1/2/3), null for missing selections
    return orderedNames.map((name) => {
      const value = byName.get(name);
      return typeof value === 'string' ? value : null;
    });
  }

  function findVariant(variants, selectedOptions) {
    if (!Array.isArray(variants) || !Array.isArray(selectedOptions)) return null;
    if (selectedOptions.some((v) => v === null)) return null;

    return variants.find(
      (v) =>
        Array.isArray(v.options) &&
        v.options.length >= selectedOptions.length &&
        selectedOptions.every((opt, idx) => v.options[idx] === opt)
    ) || null;
  }

  function setFormVariantId(root, variantId) {
    const idInput = root.querySelector(`${FORM_SELECTOR} ${ID_INPUT_SELECTOR}`);
    if (idInput) idInput.value = String(variantId);
  }

  function updatePriceHTML(root, priceHtmlMap, variantId) {
    const priceContainer = root.querySelector(PRICE_CONTAINER_SELECTOR);
    if (!priceContainer) return;

    const html = priceHtmlMap?.[String(variantId)] || priceHtmlMap?.[Number(variantId)] || '';
    if (html) priceContainer.innerHTML = html;
  }

  /**
   * Check if a variant SKU matches any special price rule and return total stacked discount amount in cents
   * @param {string} sku - Variant SKU to check
   * @param {number} priceInCents - Variant price in cents (required for percent discounts)
   * @returns {number} Total discount amount in cents from all matching rules, or 0 if no match
   */
  function getSpecialPriceDiscount(sku, priceInCents = 0) {
    if (!window?.specialPrice?.enabled || !sku) {
      return 0;
    }

    // Helper to parse SKU string (comma or newline separated)
    const parseSkuString = (skuString) => {
      if (!skuString) return [];
      return skuString
        .split(/[,\n]/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    };

    // Stack discounts from all matching rules
    let totalDiscountCents = 0;
    const rules = ['rule1', 'rule2', 'rule3', 'rule4', 'rule5'];
    
    for (const ruleKey of rules) {
      const rule = window.specialPrice[ruleKey];
      if (rule?.enabled && rule.skuString) {
        const skus = parseSkuString(rule.skuString);
        
        if (skus.includes(sku)) {
          const discountType = rule.discountType || 'amount';
          
          if (discountType === 'percent' && priceInCents > 0) {
            // Calculate percent discount
            const percent = Number(rule.discountPercent || 0);
            if (!isNaN(percent) && percent > 0) {
              const discountCents = Math.round((priceInCents * percent) / 100);
              totalDiscountCents += discountCents;
            }
          } else {
            // Use fixed amount discount (convert dollars to cents)
            const amount = Number(rule.discountAmount || 0);
            if (!isNaN(amount) && amount > 0) {
              const discountCents = Math.round(amount * 100);
              totalDiscountCents += discountCents;
            }
          }
        }
      }
    }

    return totalDiscountCents;
  }

  function updateSubmitButton(root, variant) {
    if (!variant) return;

    const submitButton = root.querySelector(SUBMIT_BUTTON_SELECTOR);
    if (!submitButton) return;

    // Auto-discount support
    const hasAutoDiscountPriceMarkup = !!root.querySelector(AUTO_DISCOUNT_SELECTOR);
    const shouldUseAutoDiscount = !!window?.autoDiscount?.enabled && hasAutoDiscountPriceMarkup;

    // Special price support - pass variant price for percent discount calculations
    const specialPriceDiscountCents = getSpecialPriceDiscount(variant.sku, variant.price);
    const hasSpecialPrice = specialPriceDiscountCents > 0;

    let finalPrice = variant.price;
    let originalPrice = variant.price;

    // Apply auto discount first if enabled
    if (shouldUseAutoDiscount) {
      const discountPercent = Number(window.autoDiscount?.percent || 0);
      const discountAmount = Math.round((variant.price * discountPercent) / 100);
      finalPrice = variant.price - discountAmount;
      originalPrice = variant.price;
    }

    // Apply special price discount on top (in cents)
    if (hasSpecialPrice) {
      finalPrice = finalPrice - specialPriceDiscountCents;
      // If no auto discount was applied, use variant price as original
      if (!shouldUseAutoDiscount) {
        originalPrice = variant.price;
      }
    }

    // Set button attributes
    submitButton.setAttribute(ATTR_DATA_PRICE, String(finalPrice));
    // Always show price in button when there's a price
    if (finalPrice) {
      submitButton.setAttribute(ATTR_DATA_SHOW_PRICE_IN_BUTTON, 'true');
    } else {
      submitButton.removeAttribute(ATTR_DATA_SHOW_PRICE_IN_BUTTON);
    }
    // Set original price only if there's a discount
    if (finalPrice < originalPrice) {
      submitButton.setAttribute(ATTR_DATA_ORIGINAL_PRICE, String(originalPrice));
    } else {
      submitButton.removeAttribute(ATTR_DATA_ORIGINAL_PRICE);
    }

    if (shouldUseAutoDiscount) {
      submitButton.setAttribute(ATTR_DATA_AUTO_DISCOUNT, 'true');
    } else {
      submitButton.removeAttribute(ATTR_DATA_AUTO_DISCOUNT);
    }

    if (hasSpecialPrice) {
      submitButton.setAttribute('data-special-price', 'true');
    } else {
      submitButton.removeAttribute('data-special-price');
    }

    submitButton.toggleAttribute(ATTR_DISABLED, !variant.available);

    // Update button label via theme logic
    if (typeof root.toggleSubmitButton === 'function') {
      root.toggleSubmitButton(
        !variant.available,
        !variant.available ? (window?.variantStrings?.soldOut || DEFAULT_SOLD_OUT) : undefined
      );
    }
  }

  function setUnavailable(root) {
    if (typeof root.toggleSubmitButton === 'function') {
      root.toggleSubmitButton(true, window?.variantStrings?.unavailable || DEFAULT_UNAVAILABLE);
    } else {
      const submitButton = root.querySelector(SUBMIT_BUTTON_SELECTOR);
      if (submitButton) submitButton.setAttribute(ATTR_DISABLED, ATTR_DISABLED);
    }
  }

  function getOrderedOptionNames(variantSelectsEl) {
    const ordered = [];
    variantSelectsEl.querySelectorAll(OPTION_SELECTOR).forEach((el) => {
      const name = el.getAttribute(ATTR_NAME);
      if (name && !ordered.includes(name)) ordered.push(name);
    });
    return ordered;
  }

  function updateOptionAvailability(variantSelectsEl, variants) {
    if (!Array.isArray(variants)) return false;

    const orderedNames = getOrderedOptionNames(variantSelectsEl);
    const selected = getSelectedOptions(variantSelectsEl);
    let changedSelection = false;

    orderedNames.forEach((name, idx) => {
      const radios = Array.from(variantSelectsEl.querySelectorAll(`input[type="radio"][name="${CSS.escape(name)}"]`));
      const select = variantSelectsEl.querySelector(`select[name="${CSS.escape(name)}"]`);

      const isValueAvailable = (value) =>
        variants.some((v) => {
          if (!v?.available || !Array.isArray(v.options) || v.options[idx] !== value) return false;
          return selected.every((sel, j) => j === idx || sel === null || v.options[j] === sel);
        });

      if (radios.length) {
        radios.forEach((radio) => {
          const available = isValueAvailable(radio.value);
          // Don't disable options - allow selection of sold-out variants
          // Just update class for styling purposes
          radio.classList.toggle(CLASS_DISABLED, !available);
          // Also add 'unavailable' class to match Liquid template
          radio.classList.toggle('unavailable', !available);
        });

        // Don't auto-switch away from unavailable selections
        // Allow users to select sold-out variants to see "SOLD OUT" in button
      } else if (select) {
        // For select dropdowns, we don't disable options
        // Allow selection of sold-out variants to show "SOLD OUT" in button
        // Note: option elements don't support classList, so we skip styling them
      }
    });

    return changedSelection;
  }

  function initRoot(root) {
    const variantSelects = root.querySelector(VARIANT_SELECTS_TAG);
    if (!variantSelects) return;

    const variants = parseJSONScript(root, VARIANTS_SCRIPT_SELECTOR);
    const pricePairs = parseJSONScript(root, PRICE_SCRIPT_SELECTOR) || [];
    const priceHtmlMap = Array.isArray(pricePairs)
      ? pricePairs.reduce((acc, item) => {
          if (item && (item.id || item.id === 0)) acc[String(item.id)] = item.html || '';
          return acc;
        }, {})
      : {};

    let isApplying = false;
    const apply = () => {
      if (isApplying) return;
      isApplying = true;

      const selectionAutoFixed = updateOptionAvailability(variantSelects, variants);
      if (selectionAutoFixed) {
        isApplying = false;
        variantSelects.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const selectedOptions = getSelectedOptions(variantSelects);
      const variant = findVariant(variants, selectedOptions);

      if (!variant) {
        setUnavailable(root);
        isApplying = false;
        return;
      }

      setFormVariantId(root, variant.id);
      updatePriceHTML(root, priceHtmlMap, variant.id);
      updateSubmitButton(root, variant);
      isApplying = false;
    };

    variantSelects.addEventListener('change', apply);
    variantSelects.addEventListener('input', apply);
    apply();
  }

  function togglePreloader(root, show) {
    if (!root) return;
    root.querySelectorAll(PRELOADER_SELECTOR).forEach((el) => {
      el.classList.toggle(CLASS_HIDE, !show);
    });
  }

  function setImages(root, imageSrc) {
    if (!root) return;

    root.querySelectorAll(IMAGE_WRAPPER_SELECTOR).forEach((wrapper) => {
      const img = wrapper.querySelector(IMAGE_SELECTOR);
      if (img) {
        img.src = imageSrc || '';
        wrapper.classList.toggle(CLASS_HIDE, !imageSrc);
      }
    });

    togglePreloader(root, false);
  }

  function clearImages(root) {
    if (!root) return;
    root.querySelectorAll(IMAGE_WRAPPER_SELECTOR).forEach((wrapper) => {
      const img = wrapper.querySelector(IMAGE_SELECTOR);
      if (img) {
        img.src = '';
      }
      wrapper.classList.add(CLASS_HIDE);
    });
  }

  function initPreloader(root) {
    if (!root) return;
    togglePreloader(root, true);
    root.querySelectorAll(IMAGE_WRAPPER_SELECTOR).forEach((wrapper) => {
      wrapper.classList.add(CLASS_HIDE);
    });
  }

  // Expose API
  const getRoot = (rootSelector) => {
    const root = typeof rootSelector === 'string' ? document.querySelector(rootSelector) : rootSelector;
    return root || document.querySelector(SELECTOR);
  };

  window.VariantPickerCustom = window.VariantPickerCustom || {};
  window.VariantPickerCustom.showPreloader = (rootSelector) => togglePreloader(getRoot(rootSelector), true);
  window.VariantPickerCustom.hidePreloader = (rootSelector) => togglePreloader(getRoot(rootSelector), false);
  window.VariantPickerCustom.setImages = (imageSrc, rootSelector) => setImages(getRoot(rootSelector), imageSrc);
  window.VariantPickerCustom.clearImages = (rootSelector) => clearImages(getRoot(rootSelector));
  window.VariantPickerCustom.init = (rootSelector) => initPreloader(getRoot(rootSelector));

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(SELECTOR).forEach((root) => {
      initRoot(root);
      initPreloader(root);
    });
  });
})();
